import React, { useState, useEffect } from 'react';
import {
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from '../services/api';
import './ManageAnnouncementsModal.css';

const ManageAnnouncementsModal = ({ isOpen, onClose }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    datePosted: new Date().toISOString().split('T')[0],
    expiryDate: '',
    status: 'published'
  });

  useEffect(() => {
    if (isOpen) {
      fetchAnnouncements();
    }
  }, [isOpen]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const { response, data } = await getAllAnnouncements();
      if (response.ok && data.success) {
        setAnnouncements(data.announcements);
      }
    } catch (error) {
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      let result;

      if (editingId) {
        result = await updateAnnouncement(editingId, {
          ...formData,
          expiryDate: formData.expiryDate || null,
          isFeatured: false
        });
      } else {
        result = await createAnnouncement({
          ...formData,
          expiryDate: formData.expiryDate || null,
          isFeatured: false
        });
      }

      if (result.response.ok && result.data.success) {
        setSuccess(editingId ? 'Announcement updated!' : 'Announcement created!');
        resetForm();
        fetchAnnouncements();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.data.message || 'Operation failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title,
      content: announcement.content,
      datePosted: new Date(announcement.datePosted).toISOString().split('T')[0],
      expiryDate: announcement.expiryDate ? new Date(announcement.expiryDate).toISOString().split('T')[0] : '',
      status: announcement.status
    });
    setEditingId(announcement._id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await deleteAnnouncement(id);
      if (result.response.ok && result.data.success) {
        setSuccess('Announcement deleted!');
        fetchAnnouncements();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.data.message || 'Delete failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      datePosted: new Date().toISOString().split('T')[0],
      expiryDate: '',
      status: 'published'
    });
    setEditingId(null);
  };

  const handleClose = () => {
    resetForm();
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="manage-modal-overlay" onClick={handleClose}>
      <div className="manage-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="manage-modal-close" onClick={handleClose}>&times;</button>
        <h2>Manage Announcements</h2>

        {error && <div className="manage-form-error">{error}</div>}
        {success && <div className="manage-form-success">{success}</div>}

        <form onSubmit={handleSubmit} className="manage-announcement-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
              rows="5"
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="datePosted">Date Posted</label>
              <input
                type="date"
                id="datePosted"
                name="datePosted"
                value={formData.datePosted}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="expiryDate">Expiry Date (Optional)</label>
              <input
                type="date"
                id="expiryDate"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="manage-form-actions">
            {editingId && (
              <button
                type="button"
                className="cancel-button"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel Edit
              </button>
            )}
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : editingId ? 'Update' : 'Create'} Announcement
            </button>
          </div>
        </form>

        <div className="announcements-list-section">
          <h3>Existing Announcements</h3>
          {loading && announcements.length === 0 ? (
            <div className="loading-text">Loading...</div>
          ) : announcements.length === 0 ? (
            <div className="empty-text">No announcements yet</div>
          ) : (
            <div className="announcements-table">
              {announcements.map((announcement) => (
                <div key={announcement._id} className="announcement-row">
                  <div className="announcement-info">
                    <div className="announcement-header">
                      <strong>{announcement.title}</strong>
                      {announcement.isFeatured && <span className="featured-badge">Featured</span>}
                      <span className={`status-badge ${announcement.status}`}>{announcement.status}</span>
                    </div>
                    <div className="announcement-meta">
                      Posted: {formatDate(announcement.datePosted)}
                      {announcement.expiryDate && ` | Expires: ${formatDate(announcement.expiryDate)}`}
                    </div>
                  </div>
                  <div className="announcement-actions">
                    <button
                      className="edit-button"
                      onClick={() => handleEdit(announcement)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(announcement._id)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageAnnouncementsModal;

